import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Params,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { LoggerService } from '../services';
import { BBWTMessageService } from '../services';
import { SecurityService } from '../services';
import { TaggedLogger } from '../services/taggedLogger';
import { UserService } from '../services';
import { OrganisationTypeEnum } from '../enums/organisation-types';
import { SaasService } from '../utils/saas.service';

@Injectable({ providedIn: 'root' })
export class SecurityGuard implements CanActivate, CanActivateChild {
  private logger: TaggedLogger;

  constructor(
    private router: Router,
    private securityService: SecurityService,
    private messageService: BBWTMessageService,
    private userService: UserService,
    private saasService: SaasService,
    loggerService: LoggerService,
  ) {
    this.logger = loggerService.taggedLogger('SecurityGuard');
  }

  private fallbackUrl = '/account/login';
  private fallbackUrlLoggedIn = '/app'; // todo: if logged in we should redirect here

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    let path = [state.url];
    let params = route.params;

    if (Object.keys(route.children).length) {
      params = route.children[0].params;
    }

    if (Object.keys(params).length) {
      path.push(this.constructPathFromRoute(route));
      path.push(this.resolveLazyParams(state.url, params));
    }

    path = path.map((p) => this.removeQueryParameters(p));

    this.logger.trace('canActivate()', path, params);

    return this.securityService
      .pageIsAvailable(path, route.data.securityHandler || null)
      .then((result) => {
        if (result) return true;

        return this.onFail(path);
      })
      .catch((error) => {
        this.logger.warn('Caught error during activation', error);
        return this.onFail(path);
      });
  }

  private onFail(path: string[]) {
    this.logger.warn('No Page access!', path);
    this.redirectToSafePage(path);
    return false;
  }

  private async redirectToSafePage(path: string[]) {
    let user = null;
    try {
      user = await this.userService.getLoggedUser();
    } catch (error) {
      this.logger.warn('User is likely not logged in');
    }

    if (user !== null) {
      this.logger.debug('Redirecting to ', this.fallbackUrlLoggedIn);
      // todo: deconstruct this
      if (
        path.filter((x) => x.lastIndexOf('/app/vmeuserdetails', 0) === 0).length !== path.length &&
        path.filter((x) => x.lastIndexOf('/app/vmeusers', 0) === 0).length !== path.length
      ) {
        this.messageService.ShowWarning('Security', 'No page access');
      }

      if (this.saasService.redirectToSaasIfSaasUser(user.organisationType)) {
        return;
      }

      return this.router.navigate([this.fallbackUrlLoggedIn]);
    }

    this.logger.debug('Redirecting to ', this.fallbackUrl);
    let queryParams = { redirectTo: path[0] };
    this.logger.debug('with params ', queryParams);
    if (!(path && path.length && path[0] === '/app')) {
      this.messageService.ShowWarning('Security', 'Please log in');
    }
    return this.router.navigate([this.fallbackUrl], { queryParams });
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    return this.canActivate(childRoute, state);
  }

  private constructPathFromRoute(route: ActivatedRouteSnapshot): string {
    let result;
    let current = route;
    while (current && current.routeConfig) {
      result = result ? `${current.routeConfig.path}/${result}` : `${current.routeConfig.path}`;
      current = current.parent;
    }
    return `/${result}`;
  }

  private resolveLazyParams(url: string, params: Params) {
    let result = url;
    const keys = Object.keys(params);
    for (let key of keys) {
      const value = params[key];
      try {
        result = result.split(value).join(`:${key}`);
      } catch (ex) {
        this.logger.warn('Could not resolve route params:', ex);
      }
    }
    return result;
  }

  /**
   * Removes query parameters from a route to ensure we don't
   * consider routes with different query parameters as having
   * different security levels.
   *
   * For example:
   * - `app/vmeinbox?selectedTab=1&stageId=1`
   *  - returns `app/vmeinbox`
   *
   * @param p The resolved path
   * @return The resolved path without any query parameters
   * @private
   */
  private removeQueryParameters(p: string) {
    if (!p || typeof p !== 'string') return p;

    const lastIndexOfQuestionMark = p.lastIndexOf('?');
    if (lastIndexOfQuestionMark === -1) {
      return p;
    }

    return p.substring(0, lastIndexOfQuestionMark);
  }
}
